import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import QRCode from 'qrcode'
import { all_routes } from '../../router/all_routes'

const romanMonths = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
]

type ClassType = 'LIVE_TEACHING' | 'E_COURSE'

type MaterialRow = {
  topic: string
  jpl: string
  speaker: string
}

const defaultMaterials: MaterialRow[] = [
  {
    topic: 'Materi A Materi A Materi A Materi A',
    jpl: '2 JP',
    speaker: 'apt. Gusti Firdaus AHL, S.Farm',
  },
  {
    topic: 'Materi B Materi B Materi B Materi B',
    jpl: '2 JP',
    speaker: 'apt. Ahmad Muzammil, S.Farm',
  },
  {
    topic: 'Materi C Materi C Materi C Materi C',
    jpl: '2 JP',
    speaker: 'apt. Poppy Yolanda P, S.Farm',
  },
]

const formatCertificateNumber = (sequence: string, dateIso: string) => {
  const date = dayjs(dateIso)
  const monthRoman = romanMonths[date.month()] || 'I'
  const year = date.year()
  const padded = sequence.padStart(3, '0')
  return `${padded}/JP-SF/${monthRoman}/${year}`
}

const CertificateBlankoPreview: React.FC = () => {
  const [sequence, setSequence] = useState('1')
  const [classType, setClassType] = useState<ClassType>('LIVE_TEACHING')
  const [participantName, setParticipantName] = useState(
    'apt. Afrinanda Rudi, S.Farm',
  )
  const [courseTitle, setCourseTitle] = useState(
    'Masterclass Skrining Hipotiroid Kongenital (SHK) pada Neonatus: Critical Error Sampling hingga Interpretasi Hasil',
  )
  const [eventDates, setEventDates] = useState(
    'Held on 30, 31 May and 1 June 2026',
  )
  const [organizer, setOrganizer] = useState('PT. Jadi Praktisi Indonesia')
  const [location, setLocation] = useState('Bandar Lampung')
  const [lastClassDate, setLastClassDate] = useState(
    dayjs().format('YYYY-MM-DD'),
  )
  const [downloadDate, setDownloadDate] = useState(
    dayjs().format('YYYY-MM-DD'),
  )
  const [jplTotal, setJplTotal] = useState('12 JP')
  const [jplNote, setJplNote] = useState('1 JPL: 45 minutes')
  const [materials, setMaterials] = useState<MaterialRow[]>(defaultMaterials)
  const [cbtEnabled, setCbtEnabled] = useState(true)
  const [cbtScore, setCbtScore] = useState('95')
  const [cbtPassingGrade, setCbtPassingGrade] = useState('80')
  const [qrDataUrl, setQrDataUrl] = useState('')

  const signatureDate =
    classType === 'LIVE_TEACHING' ? lastClassDate : downloadDate

  const certificateNumber = useMemo(
    () => formatCertificateNumber(sequence, signatureDate),
    [sequence, signatureDate],
  )

  const evaluationStatus = useMemo(() => {
    if (!cbtEnabled) return { label: 'N/A', passed: null }
    const score = Number(cbtScore)
    const passing = Number(cbtPassingGrade)
    if (Number.isNaN(score) || Number.isNaN(passing)) {
      return { label: 'N/A', passed: null }
    }
    const passed = score >= passing
    return { label: passed ? 'Passed' : 'Not Passed', passed }
  }, [cbtEnabled, cbtScore, cbtPassingGrade])

  useEffect(() => {
    const url = `${window.location.origin}${
      all_routes.publicCertificateQuery
    }?number=${encodeURIComponent(certificateNumber)}`

    QRCode.toDataURL(url, { margin: 1, width: 160 })
      .then((dataUrl) => setQrDataUrl(dataUrl))
      .catch(() => setQrDataUrl(''))
  }, [certificateNumber])

  const addMaterialRow = () => {
    setMaterials((prev) => [
      ...prev,
      { topic: 'Materi Baru', jpl: '1 JP', speaker: 'Nama Narasumber' },
    ])
  }

  const updateMaterialRow = (
    index: number,
    key: keyof MaterialRow,
    value: string,
  ) => {
    setMaterials((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [key]: value }
      return next
    })
  }

  const removeMaterialRow = (index: number) => {
    setMaterials((prev) => prev.filter((_, i) => i !== index))
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="certificate-preview">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Sans+3:wght@400;600;700&display=swap');

        :root {
          --jp-blue: #0aa1dc;
          --jp-blue-dark: #0b7fb2;
          --jp-yellow: #f9c53b;
          --jp-navy: #1e2330;
          --jp-gray: #5b6472;
          --jp-soft: #f5fbff;
        }

        .certificate-preview {
          background: #f1f5f9;
          padding: 24px;
          min-height: 100vh;
          font-family: 'Source Sans 3', sans-serif;
          color: var(--jp-navy);
        }

        .certificate-controls {
          max-width: 1040px;
          margin: 0 auto 20px;
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          display: grid;
          gap: 16px;
          box-shadow: 0 12px 30px rgba(10, 20, 40, 0.08);
        }

        .certificate-controls h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
        }

        .form-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        }

        .form-field label {
          display: block;
          font-weight: 600;
          margin-bottom: 6px;
          font-size: 13px;
          color: var(--jp-gray);
        }

        .form-field input,
        .form-field textarea,
        .form-field select {
          width: 100%;
          border: 1px solid #d7e0ea;
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 14px;
          font-family: 'Source Sans 3', sans-serif;
        }

        .form-field textarea {
          min-height: 70px;
          resize: vertical;
        }

        .control-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn {
          border: none;
          border-radius: 10px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-primary {
          background: var(--jp-blue);
          color: #ffffff;
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--jp-blue);
          color: var(--jp-blue);
        }

        .certificate-frame {
          max-width: 1040px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 28px;
          position: relative;
          overflow: hidden;
          padding: 44px 52px 56px;
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
        }

        .corner-accent {
          position: absolute;
          width: 140px;
          height: 140px;
          border-radius: 40px;
          z-index: 1;
        }

        .corner-accent.top-left {
          top: -60px;
          left: -60px;
          background: var(--jp-blue);
          transform: rotate(10deg);
        }

        .corner-accent.top-right {
          top: -70px;
          right: -70px;
          background: var(--jp-yellow);
          transform: rotate(-8deg);
        }

        .corner-accent.bottom-left {
          bottom: -70px;
          left: -70px;
          background: var(--jp-yellow);
          transform: rotate(-8deg);
        }

        .corner-accent.bottom-right {
          bottom: -60px;
          right: -60px;
          background: var(--jp-blue);
          transform: rotate(10deg);
        }

        .certificate-watermark {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at center, rgba(10, 161, 220, 0.08), transparent 55%);
          z-index: 0;
        }

        .certificate-content {
          position: relative;
          z-index: 2;
          text-align: center;
        }

        .certificate-header img {
          height: 48px;
          margin-bottom: 10px;
        }

        .certificate-header h1 {
          font-family: 'Playfair Display', serif;
          letter-spacing: 3px;
          font-size: 42px;
          margin: 8px 0 6px;
        }

        .certificate-number {
          font-size: 14px;
          color: var(--jp-gray);
          margin-bottom: 18px;
        }

        .recipient-name {
          font-size: 22px;
          font-weight: 700;
          margin: 8px 0 6px;
        }

        .certificate-body {
          font-size: 15px;
          color: var(--jp-gray);
          margin: 0 auto 14px;
          max-width: 720px;
        }

        .course-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--jp-navy);
          margin: 6px auto 10px;
          max-width: 760px;
        }

        .event-meta {
          font-size: 14px;
          color: var(--jp-gray);
          margin-bottom: 16px;
        }

        .material-table {
          width: 100%;
          border-collapse: collapse;
          margin: 18px 0 14px;
          font-size: 13px;
        }

        .material-table thead th {
          background: var(--jp-blue);
          color: #ffffff;
          padding: 8px 10px;
          text-align: left;
        }

        .material-table tbody td {
          padding: 8px 10px;
          border-bottom: 1px solid #e7eef6;
        }

        .material-table tbody tr:nth-child(even) {
          background: var(--jp-soft);
        }

        .material-table tfoot td {
          font-weight: 700;
          padding: 8px 10px;
          background: #eaf6ff;
        }

        .evaluation {
          display: flex;
          justify-content: center;
          gap: 6px;
          flex-wrap: wrap;
          font-size: 14px;
          margin-top: 8px;
        }

        .evaluation strong {
          color: var(--jp-navy);
        }

        .signature-block {
          margin-top: 26px;
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 20px;
          align-items: end;
        }

        .signature-meta {
          text-align: left;
          font-size: 13px;
          color: var(--jp-gray);
        }

        .signature-box {
          border: 1px solid #c8d5e3;
          border-radius: 12px;
          padding: 10px;
          display: flex;
          align-items: center;
          gap: 10px;
          justify-content: center;
          min-height: 90px;
        }

        .signature-box img {
          width: 70px;
          height: 70px;
          object-fit: contain;
        }

        .signature-name {
          font-weight: 700;
          margin-top: 12px;
        }

        .note-text {
          font-size: 12px;
          color: var(--jp-gray);
          margin-top: 4px;
          text-align: left;
        }

        .material-controls {
          display: grid;
          gap: 8px;
          margin-top: 8px;
        }

        .material-row {
          display: grid;
          grid-template-columns: 2fr 1fr 2fr auto;
          gap: 8px;
          align-items: center;
        }

        .material-row button {
          padding: 6px 10px;
        }

        @media print {
          body {
            background: #ffffff;
          }

          .certificate-preview {
            padding: 0;
          }

          .certificate-controls {
            display: none;
          }

          .certificate-frame {
            box-shadow: none;
            border-radius: 0;
            width: 210mm;
            min-height: 297mm;
            margin: 0 auto;
            padding: 42mm 26mm 32mm;
          }

          @page {
            size: A4 portrait;
            margin: 0;
          }
        }
      `}</style>

      <div className="certificate-controls">
        <h3>Pengaturan Sertifikat</h3>
        <div className="form-grid">
          <div className="form-field">
            <label>Jenis Kelas</label>
            <select
              value={classType}
              onChange={(e) => setClassType(e.target.value as ClassType)}
            >
              <option value="LIVE_TEACHING">Live Teaching</option>
              <option value="E_COURSE">E-Course</option>
            </select>
          </div>
          <div className="form-field">
            <label>Nomor Urut Sertifikat (XXX)</label>
            <input
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              placeholder="001"
            />
          </div>
          <div className="form-field">
            <label>Nama Peserta</label>
            <input
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Judul Course</label>
            <textarea
              value={courseTitle}
              onChange={(e) => setCourseTitle(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Held On (manual)</label>
            <textarea
              value={eventDates}
              onChange={(e) => setEventDates(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Jumlah JPL</label>
            <input
              value={jplTotal}
              onChange={(e) => setJplTotal(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Keterangan JPL</label>
            <input
              value={jplNote}
              onChange={(e) => setJplNote(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Organizer</label>
            <input
              value={organizer}
              onChange={(e) => setOrganizer(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Lokasi TTD</label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Tanggal Kelas Terakhir (Live Teaching)</label>
            <input
              type="date"
              value={lastClassDate}
              onChange={(e) => setLastClassDate(e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Tanggal Generate/Unduh (E-Course)</label>
            <input
              type="date"
              value={downloadDate}
              onChange={(e) => setDownloadDate(e.target.value)}
            />
          </div>
        </div>

        <div className="form-field">
          <label>Materi & Narasumber</label>
          <div className="material-controls">
            {materials.map((row, index) => (
              <div key={index} className="material-row">
                <input
                  value={row.topic}
                  onChange={(e) =>
                    updateMaterialRow(index, 'topic', e.target.value)
                  }
                  placeholder="Topik"
                />
                <input
                  value={row.jpl}
                  onChange={(e) =>
                    updateMaterialRow(index, 'jpl', e.target.value)
                  }
                  placeholder="JPL"
                />
                <input
                  value={row.speaker}
                  onChange={(e) =>
                    updateMaterialRow(index, 'speaker', e.target.value)
                  }
                  placeholder="Narasumber"
                />
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => removeMaterialRow(index)}
                >
                  Hapus
                </button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-outline" onClick={addMaterialRow}>
              Tambah Materi
            </button>
          </div>
        </div>

        <div className="form-grid">
          <div className="form-field">
            <label>Aktifkan CBT</label>
            <select
              value={cbtEnabled ? 'yes' : 'no'}
              onChange={(e) => setCbtEnabled(e.target.value === 'yes')}
            >
              <option value="yes">Ya</option>
              <option value="no">Tidak</option>
            </select>
          </div>
          <div className="form-field">
            <label>Nilai CBT</label>
            <input
              value={cbtScore}
              onChange={(e) => setCbtScore(e.target.value)}
              disabled={!cbtEnabled}
            />
          </div>
          <div className="form-field">
            <label>Passing Grade</label>
            <input
              value={cbtPassingGrade}
              onChange={(e) => setCbtPassingGrade(e.target.value)}
              disabled={!cbtEnabled}
            />
          </div>
        </div>

        <div className="control-actions">
          <button className="btn btn-primary" onClick={handlePrint}>
            Download PDF (Print)
          </button>
        </div>
      </div>

      <div className="certificate-frame">
        <div className="corner-accent top-left" />
        <div className="corner-accent top-right" />
        <div className="corner-accent bottom-left" />
        <div className="corner-accent bottom-right" />
        <div className="certificate-watermark" />

        <div className="certificate-content">
          <div className="certificate-header">
            <img
              src="/assets/certificates/logo%20jp%20png.png"
              alt="Jadi Praktisi"
            />
            <h1>CERTIFICATE</h1>
            <div className="certificate-number">
              Number: {certificateNumber}
            </div>
          </div>

          <div className="recipient-name">{participantName}</div>
          <div className="certificate-body">
            Has successfully completed
          </div>
          <div className="course-title">{courseTitle}</div>
          <div className="event-meta">{eventDates}</div>
          <div className="event-meta">Organized by {organizer}</div>

          <table className="material-table">
            <thead>
              <tr>
                <th style={{ width: '6%' }}>No</th>
                <th>Topic</th>
                <th style={{ width: '18%' }}>Time Studied (JPL)</th>
                <th style={{ width: '30%' }}>Speaker</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((row, index) => (
                <tr key={`${row.topic}-${index}`}>
                  <td>{index + 1}</td>
                  <td>{row.topic}</td>
                  <td>{row.jpl}</td>
                  <td>{row.speaker}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Total</td>
                <td colSpan={2}>{jplTotal}</td>
              </tr>
            </tfoot>
          </table>

          <div className="evaluation">
            <span>
              with a Score of{' '}
              <strong>{cbtEnabled ? cbtScore : '-'}</strong>
            </span>
            <span>
              ({evaluationStatus.label}) score the passing grade that{' '}
              <strong>{cbtEnabled ? cbtPassingGrade : '-'}</strong>
            </span>
          </div>

          <div className="note-text">*) {jplNote}</div>

          <div className="signature-block">
            <div className="signature-meta">
              <div>
                {location}, {dayjs(signatureDate).format('D MMMM YYYY')}
              </div>
              <div style={{ marginTop: 10 }}>
                This certificate is digitally signed by the director
              </div>
            </div>
            <div>
              <div className="signature-box">
                {qrDataUrl ? (
                  <img src={qrDataUrl} alt="QR" />
                ) : (
                  <div>QR Preview</div>
                )}
                <img
                  src="/assets/certificates/logo%20jp%20stamp%20png.png"
                  alt="Stamp"
                />
              </div>
              <div className="signature-name">apt. Rudi Afrinanda, S.Farm</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CertificateBlankoPreview
